import { Injectable } from "@angular/core";
import { UserRegion } from "../../../infrastructure/context/UserContext";
import { dateFormat } from "../../../utilities/DateUtility";
import { EquifaxGuestTableRow, GuestTableRow } from "../guests-table/models/GuestTableRow";
import { GuestVerificationCheckType, GuestVerificationStatus, GuestVerificationStatusText } from "../models/Guest";
import { GuestDetails } from "../models/GuestDetails";
import { GuestVerificationResult } from "../models/GuestVerificationResult";
import { GuestVerificationInfo } from "../models/verification-info/GuestVerificationInfo";
import { ColorProp, getColor } from "../../../layout/Colors";

@Injectable({ providedIn: 'root' })
export class GuestVerificationResultBuilder {

    buildFromGuestDetails = (guestDetails: GuestDetails, availableCheckTypes: GuestVerificationCheckType[]): GuestVerificationInfo | undefined => {
        if(!guestDetails.reservation_guest_details || guestDetails.reservation_guest_details.length === 0) {
            return undefined;
        }

        const reservationGuestDetail = guestDetails.reservation_guest_details[0];
        const verificationResultCode = reservationGuestDetail.verification_result_code;
        const documentCheckDetail = guestDetails.verification_result_details?.find(
            vr => vr.check_type === GuestVerificationCheckType.DOCUMENT
        );
        const documentCheckComplete = !documentCheckDetail ||
            (documentCheckDetail.check_result_code !== GuestVerificationStatus.PROCESSING &&
             documentCheckDetail.check_result_code !== GuestVerificationStatus.NO_VERIFICATION);
        const notStarted = this.buildVerificationResult(GuestVerificationStatus.NO_VERIFICATION, '', true);

        const verificationResult = this.buildVerificationResult(verificationResultCode, '', true);

        let identityResult;
        if(availableCheckTypes.includes(GuestVerificationCheckType.IDENTITY)) {
            identityResult = this.getVerificationResultFromGuestDetail(guestDetails, GuestVerificationCheckType.IDENTITY);
        }

        let documentResult;
        if(availableCheckTypes.includes(GuestVerificationCheckType.DOCUMENT)) {
            documentResult = this.getVerificationResultFromGuestDetail(guestDetails, GuestVerificationCheckType.DOCUMENT);
        }

        let badGuestResult;
        if(availableCheckTypes.includes(GuestVerificationCheckType.BAD_GUEST)) {
            badGuestResult = documentCheckComplete
                ? this.getVerificationResultFromGuestDetail(guestDetails, GuestVerificationCheckType.BAD_GUEST)
                : notStarted;
        }

        let backgroundResult;
        if(availableCheckTypes.includes(GuestVerificationCheckType.BACKGROUND)) {
            backgroundResult = documentCheckComplete
                ? this.getVerificationResultFromGuestDetail(guestDetails, GuestVerificationCheckType.BACKGROUND)
                : notStarted;
        }

        let watchlistResult;
        if(availableCheckTypes.includes(GuestVerificationCheckType.WATCHLIST)) {
            watchlistResult = documentCheckComplete
                ? this.getVerificationResultFromGuestDetail(guestDetails, GuestVerificationCheckType.WATCHLIST)
                : notStarted;
        }

        let sexOffenderResult;
        if(availableCheckTypes.includes(GuestVerificationCheckType.SEX_OFFENDER)) {
            sexOffenderResult = documentCheckComplete
                ? this.getVerificationResultFromGuestDetail(guestDetails, GuestVerificationCheckType.SEX_OFFENDER)
                : notStarted;
        }

        let fraudResult;
        if(availableCheckTypes.includes(GuestVerificationCheckType.FRAUD)) {
            fraudResult = documentCheckComplete
                ? this.getVerificationResultFromGuestDetail(guestDetails, GuestVerificationCheckType.FRAUD)
                : notStarted;
        }

        return {
            id: guestDetails.guest_id.toString(),
            fullName: `${guestDetails.first_name} ${guestDetails.last_name}`,
            verifiedDate: reservationGuestDetail.last_update,
            verificationResultCode: verificationResult,
            identityResultCode: identityResult || documentResult,
            badGuestResultCode: badGuestResult,
            nationalCriminalCode: backgroundResult,
            watchlistResultCode: watchlistResult,
            sexOffenderResultCode: sexOffenderResult,
            fraudResultCode: fraudResult,
            riskColor: guestDetails.risk_color,
            topRisks: guestDetails.top_risks,
            topRecommendations: guestDetails.top_recommendations,
            guestPortalCompleted: guestDetails.guest_portal_completed
        }
    }

    getVerificationResultFromGuestDetail(guestDetails: GuestDetails, checkType: GuestVerificationCheckType): GuestVerificationResult {
        const verificationResult = guestDetails.verification_result_details.find(vr => vr.check_type == checkType);
        if (!verificationResult) {
            return this.buildVerificationResult(GuestVerificationStatus.NOT_AVAILABLE, '', true);
        }

        const verificationResultCode = verificationResult.check_result_code;
        if (!verificationResultCode) {
            return this.buildVerificationResult(GuestVerificationStatus.NOT_AVAILABLE, verificationResult.note, true);
        }

        return this.buildVerificationResult(verificationResultCode, verificationResult.note, true);
    }

    buildFromGuest(guest: any, region: UserRegion): GuestTableRow {
        const documentCheckComplete = !guest.documentResultCode ||
            (guest.documentResultCode !== '4001' && guest.documentResultCode !== '5001');
        const notStarted = this.buildVerificationResult(GuestVerificationStatus.NO_VERIFICATION);

        const verificationResult = this.buildVerificationResult(guest.verificationResultCode);
        let identityResult;
        if (guest.identityResultCode != null) {
            identityResult = this.buildVerificationResult(guest.identityResultCode);
        } else {
            identityResult = this.buildVerificationResult(guest.documentResultCode);
        }
        const badGuestResult = documentCheckComplete
            ? this.buildVerificationResult(guest.badGuestResultCode) : notStarted;
        const backgroundResult = documentCheckComplete
            ? this.buildVerificationResult(guest.nationalCriminalResultCode) : notStarted;
        const watchlistResult = documentCheckComplete
            ? this.buildVerificationResult(guest.watchlistResultCode) : notStarted;
        const sexOffenderResult = documentCheckComplete
            ? this.buildVerificationResult(guest.sexOffenderResultCode) : notStarted;
        const fraudResult = documentCheckComplete
            ? this.buildVerificationResult(guest.fraudResultCode) : notStarted;

        return {
            id: guest.guestIdText,
            organizationId: guest.organizationId,
            organizationName: guest.organizationName,
            fullName: guest.fullName,
            verifiedDate: dateFormat(guest.createdDate, region),
            arrivalDate: dateFormat(guest.arrivalDate, region),
            lastEmailSent: guest.lastEmailSent,
            reservationId: guest.reservationId,
            reservationReferenceId: guest.reservationReferenceId,
            verificationResultCode: verificationResult,
            identityResultCode: identityResult,
            badGuestResultCode: badGuestResult,
            nationalCriminalCode: backgroundResult,
            watchlistResultCode: watchlistResult,
            sexOffenderResultCode: sexOffenderResult,
            fraudResultCode: fraudResult
        }
    }

    buildFromAllLegacyGuests(guest: any, region: UserRegion): GuestTableRow {
        const identityResult = this.buildVerificationResult(guest.identity_result_code);
        const badGuestResult = this.buildVerificationResult(guest.bad_guest_result_code);
        const backgroundResult = this.buildVerificationResult(guest.national_check_result_code);
        const watchlistResult = this.buildVerificationResult(guest.watch_list_result_code);
        const sexOffenderResult = this.buildVerificationResult(guest.sex_offender_result_code);

        return {
            id: guest.id,
            organizationId: guest.organization_id,
            fullName: guest.first_name + ' ' + guest.last_name,
            verifiedDate: dateFormat(guest.created_date, region),
            arrivalDate: dateFormat(guest.arrival_date, region),
            lastEmailSent: guest.email_sent_date,
            reservationId: guest.reservation_id,
            reservationReferenceId: guest.reservation_id,
            identityResultCode: identityResult,
            badGuestResultCode: badGuestResult,
            nationalCriminalCode: backgroundResult,
            watchlistResultCode: watchlistResult,
            sexOffenderResultCode: sexOffenderResult
        }
    }

    private getStatusTextFromVerificationResult(code: GuestVerificationStatus): GuestVerificationStatusText {
        switch(code) {
            case GuestVerificationStatus.PASSED:
                return GuestVerificationStatusText.PASSED;
            case GuestVerificationStatus.CONSIDER:
                return GuestVerificationStatusText.CONSIDER;
            case GuestVerificationStatus.PROCESSING:
                return GuestVerificationStatusText.PROCESSING;
            case GuestVerificationStatus.NO_VERIFICATION:
                return GuestVerificationStatusText.NO_VERIFICATION;
            default:
                return GuestVerificationStatusText.NOT_AVAILABLE;
        }
    }

    private getVerificationResultRecommendationColor(statusText: GuestVerificationStatusText): string {
        switch(statusText) {
            case GuestVerificationStatusText.PASSED:
                return getColor(ColorProp.GuestSuccess);
            case GuestVerificationStatusText.CONSIDER:
                return getColor(ColorProp.GuestConsider);
            case GuestVerificationStatusText.PROCESSING:
                return getColor(ColorProp.GuestProcessing);
            default:
                return getColor(ColorProp.GuestDefault);
        }
    }

    private getUserRecommendationBasedOnVerificationResult(statusText: GuestVerificationStatusText): string {
        return statusText === GuestVerificationStatusText.PASSED ? 'No action needed' : '';
    }

    private buildVerificationResult(
        code: GuestVerificationStatus,
        note: string = '',
        includeResultText: boolean = false
    ): GuestVerificationResult {
        const statusText = this.getStatusTextFromVerificationResult(code);
        const statusTextColor = this.getVerificationResultRecommendationColor(statusText);
        const userRecommendation = this.getUserRecommendationBasedOnVerificationResult(statusText);

        return {
            text: includeResultText ? statusText : '',
            color: statusTextColor,
            recommendation: userRecommendation,
            note: note
        }
    }

    buildGuestsFromEquifaxResults(guest: any, region: UserRegion): EquifaxGuestTableRow {
        return {
            id: guest.guestIdText,
            fullName: guest.fullName,
            verifiedDate: dateFormat(guest.createdDate, region),
            lastEmailSent: guest.lastEmailSent,
            reservationId: guest.reservationId,
            reservationReferenceId: guest.reservationReferenceId
            // TODO:  Add Equifax guest results code and text here
        }
    }
}
